import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { BarChart3, Car, Users, UserCog, MessageSquare, Shield, Menu, X, LogOut, Home } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import logo from '../../assets/logo.png';

const LINKS = [
  { label: 'Dashboard', path: '/admin', icon: BarChart3 },
  { label: 'Bookings', path: '/admin/bookings', icon: Car },
  { label: 'Users', path: '/admin/users', icon: Users },
  { label: 'Drivers', path: '/admin/drivers', icon: UserCog },
  { label: 'Contacts', path: '/admin/contacts', icon: MessageSquare },
  { label: 'Admin Management', path: '/admin/management', icon: Shield, superAdminOnly: true },
];

export default function AdminLayout() {
  const { pathname } = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const links = LINKS.filter(l => !l.superAdminOnly || user?.role === 'super_admin');

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-gray-900 text-white transform transition-transform duration-300 md:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:static md:flex md:flex-col`}>
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-700">
          <Link to="/" className="flex items-center gap-2">
            <img src={logo} alt="MDS Transportation" className="h-10 w-auto brightness-0 invert" />
          </Link>
          <button className="md:hidden text-gray-400" onClick={() => setSidebarOpen(false)}><X size={20} /></button>
        </div>
        <div className="px-3 py-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-3 py-2">Admin Panel</p>
        </div>
        <div className="flex-1 px-3 space-y-1 overflow-y-auto">
          {links.map(({ label, path, icon: Icon }) => (
            <Link key={path} to={path} onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${pathname === path ? 'bg-purple-700 text-white' : 'text-gray-400 hover:bg-gray-800 hover:text-white'}`}>
              <Icon size={18} />{label}
            </Link>
          ))}
        </div>
        <div className="p-4 border-t border-gray-700 space-y-3">
          <Link to="/" className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors">
            <Home size={16} /> View Website
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-purple-800 flex items-center justify-center text-white font-bold text-sm">
              {user?.first_name?.[0]}{user?.last_name?.[0]}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white truncate">{user?.first_name} {user?.last_name}</p>
              <p className="text-xs text-gray-400 capitalize">{user?.role?.replace('_', ' ')}</p>
            </div>
          </div>
          <button onClick={() => { logout(); navigate('/'); }} className="flex items-center gap-2 text-sm text-gray-400 hover:text-red-400 transition-colors w-full">
            <LogOut size={16} /> Logout
          </button>
        </div>
      </aside>

      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={() => setSidebarOpen(false)} />}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white h-16 px-4 md:px-6 flex items-center justify-between shadow-sm">
          <button className="md:hidden text-gray-600" onClick={() => setSidebarOpen(true)}><Menu size={22} /></button>
          <div>
            <h1 className="text-lg font-semibold text-gray-800 font-heading">MDS Admin</h1>
            <p className="text-xs text-gray-500 hidden sm:block">Transportation Management System</p>
          </div>
          <div className="text-sm text-gray-500">
            <span className="hidden sm:inline">Logged in as </span>
            <span className="font-medium text-purple-700 capitalize">{user?.role?.replace('_', ' ')}</span>
          </div>
        </header>
        <main className="flex-1 p-4 md:p-6 overflow-auto"><Outlet /></main>
      </div>
    </div>
  );
}
